/**
 * The Studio pages' line icons, traced from the owner-approved mockups. Decorative: every control
 * that carries one names itself in words.
 */

import type { ReactNode } from 'react';

interface GlyphProps {
  readonly className?: string;
}

function Stroke({
  className,
  width,
  children,
}: GlyphProps & { readonly width: number; readonly children: ReactNode }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export function StudioArrowGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className} width={2.2}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </Stroke>
  );
}

export function StudioBackGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className} width={2}>
      <path d="M15 6l-6 6 6 6" />
    </Stroke>
  );
}

export function StudioForwardGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className} width={2}>
      <path d="M9 6l6 6-6 6" />
    </Stroke>
  );
}

export function StudioCheckGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className} width={2.6}>
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </Stroke>
  );
}

export function StudioClockGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className} width={2.4}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </Stroke>
  );
}

export function StudioShieldGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className} width={1.7}>
      <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </Stroke>
  );
}

export function StudioLockGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className} width={1.8}>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </Stroke>
  );
}

export function StudioMailGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className} width={2}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </Stroke>
  );
}

export function StudioPinGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className} width={2}>
      <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z" />
      <circle cx="12" cy="10" r="2.4" />
    </Stroke>
  );
}

export function StudioGlobeGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className} width={2}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3z" />
    </Stroke>
  );
}

export function StudioPhoneGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className} width={2}>
      <path d="M6.6 3.5h2.6l1.4 4-2 1.3a11 11 0 0 0 6.6 6.6l1.3-2 4 1.4v2.6a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.6 5.7a2 2 0 0 1 2-2.2z" />
    </Stroke>
  );
}

export function StudioMinusGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className} width={2.2}>
      <path d="M5 12h14" />
    </Stroke>
  );
}

export function StudioPlusGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className} width={2.2}>
      <path d="M5 12h14M12 5v14" />
    </Stroke>
  );
}
